import React, { useState, useRef, useEffect } from 'react';
import { getOptimizedUrl, getSrcSet, getImageSizes, getPlaceholderUrl, isCloudinaryUrl } from '../lib/cloudinary';

/**
 * OptimizedImage Component
 * 
 * A performance-optimized image component with:
 * - Lazy loading with Intersection Observer
 * - Responsive srcSet for different screen sizes
 * - Blur placeholder while loading
 * - Skeleton shimmer fallback
 * - Automatic Cloudinary transforms
 */
const OptimizedImage = ({
  src,
  alt,
  variant = 'thumb',
  sizeType = 'grid',
  className = '',
  onClick,
  priority = false, // If true, loads immediately (for above-fold images)
  showSkeleton = true,
  aspectRatio = 'aspect-square', // Tailwind aspect ratio class
  objectFit = 'object-cover',
  'data-testid': testId,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || isInView) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '200px', // Start loading 200px before visible
        threshold: 0.01,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [priority, isInView]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  // Generate optimized URLs
  const optimizedSrc = getOptimizedUrl(src, variant, 'desktop');
  const srcSet = isCloudinaryUrl(src) ? getSrcSet(src, variant) : '';
  const sizes = getImageSizes(sizeType);
  const placeholderSrc = isCloudinaryUrl(src) ? getPlaceholderUrl(src) : '';

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${aspectRatio} ${className}`}
      onClick={onClick}
      data-testid={testId}
    >
      {/* Skeleton/Shimmer placeholder */}
      {showSkeleton && !isLoaded && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse">
          <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 shimmer" />
        </div>
      )}

      {/* Blur placeholder (low-quality image) */}
      {placeholderSrc && !isLoaded && isInView && (
        <img
          src={placeholderSrc}
          alt=""
          aria-hidden="true"
          className={`absolute inset-0 w-full h-full ${objectFit} blur-lg scale-110 transition-opacity duration-300`}
          style={{ opacity: isLoaded ? 0 : 1 }}
        />
      )}

      {/* Main image */}
      {isInView && !hasError && (
        <img
          ref={imgRef}
          src={optimizedSrc}
          srcSet={srcSet || undefined}
          sizes={srcSet ? sizes : undefined}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchpriority={priority ? 'high' : 'auto'}
          onLoad={handleLoad}
          onError={handleError}
          className={`w-full h-full ${objectFit} transition-opacity duration-500 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          data-testid={testId ? `${testId}-img` : undefined}
        />
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-center text-gray-400">
            <svg
              className="w-12 h-12 mx-auto mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="text-sm">Afbeelding niet beschikbaar</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;
